import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import { CarlineSelect, ConfirmacionEnvio, ConsentQuestion, DetalleMotivo, Loading, MultipleCTASelection, NombreApellidos, TelefonoEmail } from 'src/components';
import { formatPrefix, FormController, LayoutTemplate, removeWhiteSpace, useFaContent, useGetInstalaciones, useIsElectrico, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { useGetWaidLitera } from 'src/feature-app/hooks/use-get-waid-litera';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { EmpresaComercialesInfoComponent, EmpresaComercialesThankYou } from '.';
import { formatFlags, formatHeaders, formDataBuilder, getDpsFields } from '../../format-fields';
import { useInitializeLeadForm } from '../../Leads';
import { MapaEmpresas } from '../MapaEmpresa';

export function EmpresaComercialesForm() {
	const { initializeForm } = useOneFormContext();
	const store = useStore();
	const dispatch = useDispatch();
	const faContent = useFaContent();
	const waid = useGetWaidLitera();

	useGetInstalaciones('LCV_DDB_FRM_VWEMPRESAS', 'LCV_SF_LEAD_INSTALLATION', 'vn');

	const ctasObjetivo = [
		{
			label: 'Más Información',
			value: 'Más Información',
		},
		{
			label: 'Oferta comercial',
			value: 'Presupuesto',
		},
		{
			label: 'Prueba de conducción',
			value: 'Test Drive',
		},
	];

	const ctasFlota = [
		{
			label: '1 o 2',
			value: '1 - 2',
		},
		{
			label: 'De 3 a 10',
			value: '3 - 10',
		},
		{
			label: 'De 11 a 50',
			value: '11 - 50',
		},
	];

	let index = 0;

	const defaultSteps: Steps[] = [
		{
			title: (
				<>
					<Text bold>¿Qué modelo</Text> te interesa para tu flota?
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<CarlineSelect useCarline={true} />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'Modelo',
			outputs: ['modelo'],
			outputsText: ['modelo'],
		},
		{
			title: (
				<>
					<Text bold>¿Que consulta</Text> quieres realizar?
				</>
			),
			fields: <LayoutTemplate element={<MultipleCTASelection ctaValues={ctasObjetivo} name="objetivo" nextOnClick />} />,
			screenIndex: index++,
			name: 'Objetivo',
			outputs: ['objetivo'],
			outputsText: ['objetivo'],
			ctas: ctasObjetivo,
		},
		{
			screenIndex: index++,
			name: 'Mapa',
			multiStep: true,
			component: <MapaEmpresas formName="venta" showHorario={false} />,
		},
		{
			title: (
				<>
					Estamos deseando hacerte una propuesta. Pero antes, nos gustaría saber <Text bold>quién eres</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<NombreApellidos />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'NombreApellido',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
			showOverflow: true,
		},
		{
			title: (
				<>
					<Text bold>¿En qué teléfono e email</Text> podemos localizarte?
				</>
			),
			fields: (
				<LayoutTemplate
					element={<TelefonoEmail requiredTelefono requiredEmail />}
				/>
			),
			screenIndex: index++,
			name: 'EmailTelefono',
			outputs: ['cellphone', 'email'],
			outputsText: ['cellphone', '/', 'email'],
		},
		{
			title: (
				<>
					Ahora, nos gustaría conocer algunos detalles sobre <Text bold>tu empresa</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<EmpresaComercialesInfoComponent />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'Empresa',
			outputs: ['razonSocial', 'CIF', 'cargo', 'postalCode'],
			outputsText: ['razonSocial', '/', 'CIF', '/', 'cargo', '/', 'postalCode'],
		},
		{
			title: (
				<>
					<Text bold>¿Cuántos coches </Text>necesitas para tu{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						flota?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</>
			),
			fields: <LayoutTemplate element={<MultipleCTASelection ctaValues={ctasFlota} name="fleet" nextOnClick />} />,
			screenIndex: index++,
			name: 'Fleet',
			outputs: ['fleet'],
			outputsText: ['fleet'],
		},
		{
			title: (
				<>
					<Text bold>¿Quieres comentarnos</Text> algo más?
				</>
			),
			fields: <LayoutTemplate element={<DetalleMotivo CTALabel="No, eso es todo" name="comentario" />} />,
			screenIndex: index++,
			name: 'Cometario',
			outputs: ['comentario'],
			outputsText: ['comentario'],
		},
		{
			fields: <ConsentQuestion questionType="lssi" />,
			screenIndex: index++,
			name: 'lssi',
			outputs: ['lssi'],
		},
		{
			fields: <ConsentQuestion questionType="tmk" />,
			screenIndex: index++,
			name: 'tmk',
			outputs: ['tmk'],
		},
		{
			fields: <ConfirmacionEnvio />,
			screenIndex: index++,
			name: 'Confirmacion',
			isLastStep: true,
			hidePreviousStep: true,
		},
	];

	const EmpresasFormComercialesData: Partial<FormDataGroup> = {
		headers: {
			FORM_NAME: 'vw-oneform-empresas',
			DPS_TOKEN: 'LCV_DDB_FRM_VWEMPRESAS',
		},
		flags: {
			PERSON_RGPD_ADAPTED: 'true',
			FLAG_IS_MYSTERY_LEAD: 'false',
			FLAG_RELACION_MARCA_SIN_IC: 'false',
			FLAG_SF_INTEGRATION: 'true',
			FLAG_ALLOWS_DATA_ENRICHMENT: 'false',
		},
		fields: {
			formName: 'empresas',
			formType: 'contact',
			//pageCategory: 'Más Información',
			//formularioFlag: 'masInformacion',
		}, // Analítica
	};

	const sendForm = (): Promise<boolean> => {
		const { formData, dealersInfo }: OneFormState = store.getState();
		const { fields, flags, headers } = formData;
		const { selectedInstallation, carlines } = dealersInfo;
		const { VGED_CommercialName__c, ShippingStreet, VGED_DealerCode__c, ShippingStateCode, KVPSCode__c, Id } = selectedInstallation;

		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, referalUrl, url, device } = getParamsForm();

			const data = {
				...formatHeaders(headers),

				'headers[FORM_URL]': url,
				'headers[ORIGIN_DEVICE_TYPE]': device,
				'headers[FORM_DATE_INSERTION]': today,
				'headers[ORIGIN_TRACKING_CODE]': tc,
				'headers[FORM_VERSION]': `vw-oneform-empresas-${process.env.VERSION}`,
				'headers[FORM_OBJECTIVE]': fields.objetivo,

				...formatFlags(flags),

				'fields[DPS_BRAND]': 'Volkswagen CV',
				'fields[SF_LEAD_RECORDTYPE_ID]': '0121i000000D7NyAAK',
				'fields[SF_LEAD_RECORDTYPE_NAME]': 'Sales Assistant',
				'fields[SF_LEAD_SALES_INITIAL_ACTION]': 'Direct to Sales Assistant',
				'fields[PERSON_NAME]': fields.name,
				'fields[PERSON_SURNAME_1]': fields.surname,
				'fields[PERSON_SURNAME_2]': fields.secondSurname,
				'fields[PERSON_EMAIL]': removeWhiteSpace(fields.email),
				'fields[PERSON_PHONE_MOBILE_FULL]': `${formatPrefix(fields.prefix)}${fields.cellphone}`,
				'fields[PERSON_PHONE_MOBILE]': fields.cellphone,
				'fields[PERSON_PHONE_PREFIX]': formatPrefix(fields.prefix),
				'fields[PERSON_POSTAL_CP]': fields.postalCode,
				'fields[COMPANY_NAME]': fields.razonSocial,
				'fields[COMPANY_CIF]': fields.CIF,
				'fields[SURVEY_ANSWER_01]': fields.cargo,
				'fields[COMMENTS_PERSON]': 'Tamaño flota: ' + fields.fleet + (fields.comentario != '' ? ' Comentario: ' + fields.comentario : ''),
				'fields[SF_LEAD_INSTALLATION_ADDRESS]': ShippingStreet,
				'fields[SF_LEAD_INSTALLATION_CODE]': VGED_DealerCode__c,
				'fields[SF_LEAD_INSTALLATION_ID]': Id,
				'fields[SF_LEAD_INSTALLATION_NAME]': VGED_CommercialName__c,
				'fields[SF_LEAD_INSTALLATION_PROVINCE_CODE]': ShippingStateCode.toString().padStart(2, '0'),
				'fields[VEH_INTEREST_FUEL]': useIsElectrico(fields.modelo) ? 'Electric' : 'Undefined',
				'fields[VEH_INTEREST_BRAND]': 'Volkswagen CV',
				'fields[VEH_INTEREST_ID]': fields.carlineCode,
				'fields[VEH_INTEREST_MODEL]': fields.modelo,
				'fields[VEH_INTEREST_NEW_OR_USED]': 'New Car',
				'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi ? fields.lssi : 'NO INFORMADO',
				'fields[PERSON_RGPD_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
				...getDpsFields(faContent),
			};

			delete data['headers[DETALLE_TIPO_PETICION_SIM]'];
			delete data['headers[DESC_FORM_DETAIL]'];

			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const { loading, steps, firstStep, lastStep } = useInitializeLeadForm(defaultSteps);

	useEffect(() => {
		if (loading) {
			return;
		}

		const EmpresasFormComercialesInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens: steps.length,
			screenIndex: firstStep.screenIndex,
			step: firstStep,
			steps: steps,
			notALayer: false,
			formType: 'layer',
			formTheme: 'inverted',
			stepsHistory: [0],
			thankYouComponent: <EmpresaComercialesThankYou />,
		};

		initializeForm(EmpresasFormComercialesInfo, EmpresasFormComercialesData);
	}, [loading]);

	return loading ? <Loading /> : <FormController steps={steps} screenType="layer-screen" />;
}
